import { useEffect, useRef, useCallback } from 'react'
import { useAuth } from '../lib/auth.jsx'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

/**
 * Auto-logout after idleMinutes of no user activity.
 * Also prunes Sanctum token expiry from localStorage timestamp.
 *
 * Usage: <AutoLogout idleMinutes={480} />  (mount once inside Layout)
 */
export default function AutoLogout({ idleMinutes = 480 }) {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const timerRef = useRef(null)

  const IDLE_MS = idleMinutes * 60 * 1000
  const LOGIN_TS_KEY = 'lpk_cms_login_at'

  // Check if stored session is expired (page refresh scenario)
  useEffect(() => {
    if (!user) return
    const loginAt = Number(localStorage.getItem(LOGIN_TS_KEY) || 0)
    if (loginAt && Date.now() - loginAt > IDLE_MS) {
      doLogout('Sesi Anda telah berakhir. Silakan masuk kembali.')
    }
  }, [user])

  const doLogout = useCallback(async (reason = 'Sesi berakhir karena tidak aktif.') => {
    clearTimeout(timerRef.current)
    localStorage.removeItem(LOGIN_TS_KEY)
    await logout()
    toast.warning(reason)
    navigate('/login', { replace: true })
  }, [logout, navigate])

  const resetTimer = useCallback(() => {
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => doLogout(), IDLE_MS)
  }, [doLogout, IDLE_MS])

  useEffect(() => {
    if (!user) return

    // Record login timestamp if not set
    if (!localStorage.getItem(LOGIN_TS_KEY)) {
      localStorage.setItem(LOGIN_TS_KEY, String(Date.now()))
    }

    // Activity events that reset the idle timer
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll', 'click']
    events.forEach((e) => window.addEventListener(e, resetTimer, { passive: true }))
    resetTimer() // start timer immediately

    return () => {
      clearTimeout(timerRef.current)
      events.forEach((e) => window.removeEventListener(e, resetTimer))
    }
  }, [user, resetTimer])

  return null // renders nothing
}
